import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crop, Save, Upload, X } from "lucide-react";
import ImageCropDialog from "@/components/ImageCropDialog";

export default function SpaModuleOperationalSettings({ canEdit }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoCrop, setLogoCrop] = useState(null); // { file, fromExisting }

  useEffect(() => {
    api.get("/admin/modules/spa/operational-settings")
      .then((r) => setForm(r.data || {}))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || "Could not load Wellness settings"));
  }, []);

  const uploadFileOnly = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const beginLogoCrop = (file, fromExisting = false) => {
    if (!file) return;
    setLogoCrop({ file, fromExisting });
  };

  const recropLogo = async () => {
    if (!form?.spa_door_logo_source_path && !form?.spa_door_logo_url) return;
    try {
      const src = form.spa_door_logo_source_path
        ? fileUrl(form.spa_door_logo_source_path)
        : form.spa_door_logo_url;
      const r = await fetch(src, { credentials: "include", cache: "no-store" });
      if (!r.ok) throw new Error("Could not load source");
      const blob = await r.blob();
      const ext = blob.type === "image/webp" ? "webp" : blob.type === "image/jpeg" ? "jpg" : "png";
      beginLogoCrop(new File([blob], `spa-door-logo-source.${ext}`, { type: blob.type || "image/png" }), true);
    } catch {
      toast.error("Could not load the original Spa Door logo. External URLs may block re-cropping; upload the logo instead.");
    }
  };

  const commitLogoCrop = async (croppedFile) => {
    if (!logoCrop?.file) return;
    setUploadingLogo(true);
    try {
      const croppedPath = await uploadFileOnly(croppedFile);
      let sourcePath = form.spa_door_logo_source_path || null;
      if (!logoCrop.fromExisting || !sourcePath) {
        const canStoreOriginal = /\.(png|jpe?g|webp|gif)$/i.test(logoCrop.file?.name || "") && logoCrop.file?.type !== "image/svg+xml";
        sourcePath = canStoreOriginal ? await uploadFileOnly(logoCrop.file) : croppedPath;
      }
      setForm((f) => ({
        ...f,
        spa_door_logo_url: fileUrl(croppedPath),
        spa_door_logo_source_path: sourcePath,
      }));
      setLogoCrop(null);
      toast.success("Spa Door logo crop applied — save settings to publish it");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not upload Spa Door logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        global_min_notice_min: Number(form.global_min_notice_min || 0),
        prep_time_min: Number(form.prep_time_min || 0),
        spa_door_logo_size: Number(form.spa_door_logo_size || 120),
      };
      const r = await api.put("/admin/modules/spa/operational-settings", payload);
      setForm(r.data);
      toast.success("Wellness operational settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save Wellness settings");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="border border-border bg-card p-6 text-muted-foreground">Loading Wellness operational settings…</div>;

  return (
    <div className="border border-border bg-card p-5 md:p-7 space-y-6 max-w-4xl">
      <ImageCropDialog
        open={!!logoCrop}
        onOpenChange={(v) => { if (!v) setLogoCrop(null); }}
        file={logoCrop?.file || null}
        title="Crop Spa Door logo"
        defaultAspect="original"
        defaultFitMode="contain"
        defaultLongEdge={1800}
        onConfirm={commitLogoCrop}
      />

      <div>
        <div className="font-display text-2xl">Wellness operational settings</div>
        <p className="text-sm text-muted-foreground mt-1">Treatment timing, opening hours and Spa Door behaviour belong to the Wellness module.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <label className="block"><span className="text-sm font-medium">Minimum booking notice (minutes)</span><Input disabled={!canEdit} className="mt-2" type="number" min="0" value={form.global_min_notice_min ?? 120} onChange={(e) => setForm((f) => ({ ...f, global_min_notice_min: e.target.value }))} /></label>
        <label className="block"><span className="text-sm font-medium">Preparation time between treatments</span><Input disabled={!canEdit} className="mt-2" type="number" min="0" value={form.prep_time_min ?? 15} onChange={(e) => setForm((f) => ({ ...f, prep_time_min: e.target.value }))} /></label>
        <label className="block"><span className="text-sm font-medium">Opens at</span><Input disabled={!canEdit} className="mt-2 font-mono" value={form.open_time || "09:00"} onChange={(e) => setForm((f) => ({ ...f, open_time: e.target.value }))} /></label>
        <label className="block"><span className="text-sm font-medium">Closes at</span><Input disabled={!canEdit} className="mt-2 font-mono" value={form.close_time || "20:00"} onChange={(e) => setForm((f) => ({ ...f, close_time: e.target.value }))} /></label>
      </div>

      <label className="block"><span className="text-sm font-medium">Adults-only rejection message</span><Textarea disabled={!canEdit} className="mt-2" value={form.adults_only_message || ""} onChange={(e) => setForm((f) => ({ ...f, adults_only_message: e.target.value }))} /></label>

      <div className="border-t border-border pt-5 space-y-5">
        <div>
          <div className="font-medium">Spa Door branding</div>
          <p className="text-xs text-muted-foreground mt-1">The same advanced crop studio used in platform Settings is available here. The original upload is retained for future re-cropping.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <label className="block"><span className="text-sm font-medium">Spa Door guest identity</span><Select disabled={!canEdit} value={form.spa_door_identity_mode || "full_name"} onValueChange={(v) => setForm((f) => ({ ...f, spa_door_identity_mode: v }))}><SelectTrigger className="mt-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="full_name">Full name</SelectItem><SelectItem value="first_name">First name only</SelectItem><SelectItem value="cabin">Cabin only</SelectItem></SelectContent></Select></label>
          <label className="block"><span className="text-sm font-medium">Spa Door logo position</span><Select disabled={!canEdit} value={form.spa_door_logo_position || "top-center"} onValueChange={(v) => setForm((f) => ({ ...f, spa_door_logo_position: v }))}><SelectTrigger className="mt-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="top-left">Top · Left</SelectItem><SelectItem value="top-center">Top · Center</SelectItem><SelectItem value="top-right">Top · Right</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="bottom-left">Bottom · Left</SelectItem><SelectItem value="bottom-center">Bottom · Center</SelectItem><SelectItem value="bottom-right">Bottom · Right</SelectItem></SelectContent></Select></label>
        </div>

        <div>
          <span className="text-sm font-medium">Spa Door logo</span>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {form.spa_door_logo_url ? (
              <div className="relative w-40 h-20 border border-border bg-muted/30 flex items-center justify-center p-2">
                <img src={form.spa_door_logo_url} alt="Spa Door logo" className="max-w-full max-h-full object-contain" />
                {canEdit && <button type="button" onClick={() => setForm((f) => ({ ...f, spa_door_logo_url: "", spa_door_logo_source_path: null }))} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center"><X className="h-3 w-3" /></button>}
              </div>
            ) : (
              <div className="w-40 h-20 border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">No logo</div>
            )}

            {canEdit && (
              <>
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" />{uploadingLogo ? "Uploading…" : "Upload & crop"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => beginLogoCrop(e.target.files?.[0])} />
                </label>
                {form.spa_door_logo_url && <Button type="button" variant="outline" onClick={recropLogo}><Crop className="h-4 w-4 mr-2" />Crop current</Button>}
              </>
            )}
          </div>
        </div>

        <label className="block"><span className="text-sm font-medium">Or external logo URL</span><Input disabled={!canEdit} className="mt-2" value={form.spa_door_logo_url || ""} onChange={(e) => setForm((f) => ({ ...f, spa_door_logo_url: e.target.value, spa_door_logo_source_path: null }))} placeholder="https://… or /api/files/…" /></label>
        <label className="block sm:max-w-xs"><span className="text-sm font-medium">Spa Door logo height (px)</span><Input disabled={!canEdit} className="mt-2" type="number" min="48" max="320" value={form.spa_door_logo_size || 120} onChange={(e) => setForm((f) => ({ ...f, spa_door_logo_size: e.target.value }))} /></label>
      </div>

      {canEdit && <Button disabled={saving} onClick={save}><Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save Wellness settings"}</Button>}
    </div>
  );
}
